// ========================================
// COMPOSANT Navigation
// Barre de navigation principale
// ========================================

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteSettings } from '../../../hooks';
import { FiMenu, FiX } from 'react-icons/fi';
import './Navigation.css';

const Navigation = () => {
  // ========== HOOK FIREBASE ==========
  const { settings } = useSiteSettings();

  // ========== ÉTATS LOCAUX ==========
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // ========== DONNÉES DÉRIVÉES ==========
  const siteName = settings?.siteName || 'Gustave KONATE';
  const logoInitials = siteName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const logoImage = settings?.logo || null;

  // Détecter le scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fermer le menu mobile lors du changement de page
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Bloquer le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/projets', label: 'Projets' },
    { path: '/blog', label: 'Blog' },
    { path: '/a-propos', label: 'À propos' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* ========== HEADER PRINCIPAL ========== */}
      <header className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
        <nav className="main-nav container">
          {/* Logo */}
          <Link to="/" className="nav-logo">
            {logoImage ? (
              <img src={logoImage} alt={siteName} className="logo-image" />
            ) : (
              <span className="logo-text">{logoInitials}</span>
            )}
            <span className="logo-full">{siteName}</span>
          </Link>

          {/* Desktop Menu */}
          <ul className="nav-menu">
            {navLinks.map(link => (
              <li key={link.path}>
                <Link 
                  to={link.path} 
                  className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <div className="nav-cta">
            <Link to="/a-propos#contact" className="btn btn-primary btn-sm">
              Me contacter
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </nav>
      </header>

      {/* ========== MENU MOBILE (SORTI DU HEADER) ========== */}
      {/* Overlay - Fond sombre cliquable */}
      <div 
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Menu Mobile */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-menu">
          {navLinks.map(link => (
            <li key={link.path}>
              <Link 
                to={link.path} 
                className={`mobile-nav-link ${isActive(link.path) ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mobile-nav-cta">
          <Link 
            to="/a-propos#contact" 
            className="btn btn-primary"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Me contacter
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navigation;